import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { AuthImage } from "@/components/AuthImage";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Bookmark } from "lucide-react";

const OCCASIONS = ["casual", "work", "formal", "party", "sport"];

export default function Pair() {
  const [occasion, setOccasion] = useState("casual");
  const [loading, setLoading] = useState(false);
  const [outfits, setOutfits] = useState([]);
  const [saved, setSaved] = useState({});
  const [generated, setGenerated] = useState(false);

  const generate = async () => {
    setLoading(true);
    setGenerated(false);
    try {
      const { data } = await api.post("/pair/generate", { occasion });
      setOutfits(data.outfits);
      setSaved({});
      setGenerated(true);
      if (data.outfits.length === 0) toast.info("No looks found. Add more variety.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate");
    } finally {
      setLoading(false);
    }
  };

  const save = async (outfit, idx) => {
    try {
      await api.post("/outfits", {
        name: outfit.name,
        occasion,
        item_ids: outfit.item_ids,
        notes: outfit.notes,
        score: outfit.score,
      });
      setSaved((p) => ({ ...p, [idx]: true }));
      toast.success("Saved to Lookbook");
    } catch (e) {
      toast.error("Could not save");
    }
  };

  return (
    <div className="bg-bg0 min-h-screen">
      <Navbar />
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-12">
        <div className="overline mb-3 text-volt">AI Stylist Engine</div>
        <h1 className="font-display uppercase text-6xl md:text-7xl font-bold tracking-tighter mb-4">Pair.</h1>
        <p className="text-epsub max-w-xl mb-10">
          Pick an occasion and let Eternal engineer coordinated fits from your closet.
        </p>

        <div className="flex flex-wrap items-center gap-6 border-b border-epborder pb-5 mb-8" data-testid="occasion-selector">
          {OCCASIONS.map((o) => (
            <button
              key={o}
              onClick={() => setOccasion(o)}
              data-testid={`occasion-${o}`}
              className={`overline ep-btn ${occasion === o ? "text-volt" : "text-epmuted hover:text-white"}`}
            >
              {o}
            </button>
          ))}
        </div>

        <button
          onClick={generate}
          disabled={loading}
          data-testid="generate-btn"
          className="ep-btn inline-flex items-center gap-2 bg-volt text-black px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover disabled:opacity-60 mb-12 volt-glow"
        >
          {loading ? (
            <>
              <Loader2 size={17} className="animate-spin" /> Composing…
            </>
          ) : (
            <>
              <Sparkles size={17} /> Generate Outfits
            </>
          )}
        </button>

        {loading && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-[420px] ep-loading" />
            ))}
          </div>
        )}

        {!loading && generated && outfits.length === 0 && (
          <div className="border border-epborder p-12 text-center" data-testid="pair-empty">
            <p className="font-display uppercase text-3xl font-bold">No looks yet.</p>
            <p className="text-epsub mt-3">Upload more variety (tops, bottoms & kicks) to unlock pairings.</p>
          </div>
        )}

        {!loading && outfits.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="outfit-results">
            {outfits.map((o, idx) => (
              <div key={idx} className="ep-card p-6 flex flex-col" data-testid={`outfit-${idx}`}>
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <div className="overline mb-1 text-volt">{occasion}</div>
                    <h3 className="font-display uppercase text-2xl leading-tight tracking-tight">{o.name}</h3>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-4xl font-bold text-volt leading-none">{o.score}</div>
                    <div className="overline mt-1">match</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-5">
                  {o.items.map((it) => (
                    <div key={it.id} className="border border-epborder overflow-hidden">
                      <ItemThumb
                        item={it}
                        className="w-full aspect-[3/4] object-cover"
                        testId={`outfit-${idx}-item-${it.id}`}
                      />
                    </div>
                  ))}
                </div>

                <p className="text-sm text-epsub leading-relaxed flex-1">{o.notes}</p>

                <button
                  onClick={() => save(o, idx)}
                  disabled={saved[idx]}
                  data-testid={`save-outfit-${idx}`}
                  className="ep-btn mt-6 inline-flex items-center justify-center gap-2 border border-white/25 px-5 py-3 text-sm font-bold uppercase tracking-widest hover:bg-white hover:text-black disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-white"
                >
                  <Bookmark size={15} strokeWidth={1.75} />
                  {saved[idx] ? "Saved" : "Save to Lookbook"}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
